// src/app/pages/admin/useCargueCandidatos.ts
// Hook autocontenido con toda la lógica de Carga Masiva de Candidatos
// (plantilla, validación y envío), separado de CarguesMasivosWrapper.tsx
// para que Empresas/Vacantes y Candidatos se puedan desarrollar sin pisarse.
//
// Cubre "Datos del usuario" (UserManagement.tsx/Step1.tsx) MÁS "Información básica" de
// CurriculumRegistration.tsx (steps/curriculum-registration/Step1-4.tsx): cada fila del Excel
// crea, en una sola operación, User + UserComplemento + UserCurriculo + (opcional) un complemento
// de dirección + (opcional) un cargo deseado. El envío va a un controlador backend nuevo y aislado
// (CargueCandidatosController), no al /addUsersMasivo original ni a ImportDataController.
import ExcelJS from 'exceljs';
import { useEffect, useRef, useState } from 'react';
import {
	candidatos as candidatosCargue,
	CamposTipoPoblacion,
	CamposDireccionResidencia,
	CAMPO_MUNICIPIO_NACIMIENTO,
	CAMPO_DEPARTAMENTO_NACIMIENTO,
	CAMPO_MUNICIPIO_RESIDENCIA,
	CAMPO_DEPARTAMENTO_RESIDENCIA,
	CAMPO_LOCALIDAD,
	CAMPO_BARRIO,
	CAMPO_CARGO_DESEADO,
	CAMPO_NIVEL_EDUCATIVO_MAX,
	CAMPO_NIVEL_EDUCATIVO,
	CAMPO_NUCLEO_CONOCIMIENTO,
	CAMPO_AREA_DESEMPENO,
	CAMPO_TITULO_HOMOLOGADO,
	CAMPO_MUNICIPIO_EMPRESA,
	CAMPO_INSTITUCION_NIVEL_EDUCATIVO,
	CAMPO_OBSERVACIONES_NIVEL_EDUCATIVO,
	CAMPO_INSTITUCION_CAPACITACION,
} from '../../util/listas';
import parameters from '../../util/parameters';
import parameterSingleton from '../../services/Parameters.service';
import { ParameterConfigMmodel } from '../../models/parameter.model';
import {
	isValidEmail,
	isValidColombianPhoneNumber,
	isValidFirstSecondNameFirstSecondLastName,
	isValidDocumentNumber,
	checkRulesByBirthDateAndDocType,
	checkRulesByCountryAndDocumentType,
	salarioMinIsGreaterThanSalarioMax,
	concatArrayToStringJoinBy,
} from '../../util/Index';
import {
	createCargueCandidatosPost,
	getCargos,
	getBarriosByLocalidad,
	getNivelesEducativos,
	getNucleosConocimiento,
	getNivelesNucleos,
	getTitulosHomologados,
	getAreasDesempenio,
} from '../../modules/apps/user-management/users-list/core/_requests';

const DOCUMENT_TYPE_RULES: Record<string, { min: number; max: number; type: 'number' | 'text' }> = {
	CC: { min: 3, max: 11, type: 'number' },
	TI: { min: 10, max: 11, type: 'number' },
	CE: { min: 1, max: 7, type: 'number' },
	PA: { min: 1, max: 17, type: 'text' },
	PEP: { min: 15, max: 20, type: 'number' },
	PT: { min: 4, max: 7, type: 'number' },
};

// Constantes de códigos de catálogo (mismas que usa el wizard del candidato, ver Step1-3.tsx y
// UserCurriculoSchema.js) — son reglas de negocio fijas, no inventadas para este cargue.
const CODIGO_SI = '1';
const CODIGO_NO = '0';
const CODIGO_PAIS_COLOMBIA = 'CO';
const CODIGO_MUNICIPIO_BOGOTA = '11001';
const CODIGO_SEXO_MASCULINO = 'M';
const CODIGO_ESTADO_CURSO_GRADUADO = '3';
const CODIGO_ESTADO_CERTIFICADO = '2';

// Grupo de Nivel educativo (sigla_nivel_educativo) que exige Núcleo de conocimiento (único
// condicional de Step5.tsx que se mantuvo, ver nota en listas.ts sobre este campo).
const GRUPO_NIVEL_SUPERIOR = ['TP', 'T', 'PG', 'E', 'M', 'D']; // Técnica profesional, Tecnología, Universitaria, Especialización, Maestría, Doctorado

// Columnas cuyo dropdown de Excel debe restringirse a un subconjunto del catálogo completo
// (la UI del candidato solo permite estos valores hoy — ver Step2.tsx: departamentoList.filter(...)).
const FILTROS_VALORES_DROPDOWN: Record<string, (item: ParameterConfigMmodel) => boolean> = {
	[CAMPO_DEPARTAMENTO_RESIDENCIA]: (item) => item.code === '11' || item.code === '25',
	// País de residencia debe ser específicamente Colombia (ver spec de campos/reglas).
	'País de residencia': (item) => item.code === CODIGO_PAIS_COLOMBIA,
};

const resolveCodeByValue = (lista: ParameterConfigMmodel[], label: any): string | undefined => {
	const texto = String(label ?? '').trim().toLowerCase();
	if (!texto) return undefined;
	return lista.find((p) => String(p.value).trim().toLowerCase() === texto)?.code;
};

// Busca por igualdad de texto (sin distinguir mayúsculas/minúsculas ni espacios) en catálogos que NO
// tienen forma {code,value} (NivelEducativo, NucleoConocimiento, AreaDesempeno, TituloHomologado,
// Localidad, Barrio, Cargo) — mismo criterio de comparación que ya usa resolveCodeByValue.
const buscarPorTexto = <T,>(lista: T[], valor: any, attr: keyof T): T | undefined => {
	const texto = String(valor ?? '').trim().toLowerCase();
	if (!texto || !lista?.length) return undefined;
	return lista.find((item) => String(item[attr] ?? '').trim().toLowerCase() === texto);
};

interface NivelEducativoOpcion { id: number; sigla: string; desc: string }
interface CatalogoIdDesc { id: number; desc: string }
interface NivelNucleoRelacion { id: number; idNivelEducativo: number; idNucleoConocimiento: number }
interface TituloHomologadoOpcion { id: number; idNivelNucleo: number; desc: string }

// Una fila se considera "vacía" (no diligenciada por la persona) cuando ninguna de las columnas
// visibles de la plantilla tiene contenido. Es necesario filtrarlas porque las columnas ocultas de
// referencia (dropdowns) escriben valores reales de celda en muchas filas (una lista de catálogo de
// ej. 235 países ocupa las filas 1 a 236 en su columna); eso hace que sheet_to_json, al leer TODA la
// hoja, arme un objeto por cada una de esas filas aunque las columnas visibles estén en blanco —
// aparecían como filas "fantasma" con todos los obligatorios marcados en rojo.
const esFilaVacia = (row: any): boolean =>
	candidatosCargue.Headers.every((h) => row[h] == null || String(row[h]).trim() === '');

// Convierte un índice de columna (1-based) a su letra en Excel (1 -> 'A', 27 -> 'AA', etc.)
const columnLetter = (index: number): string => {
	let letter = '';
	let n = index;
	while (n > 0) {
		const rem = (n - 1) % 26;
		letter = String.fromCharCode(65 + rem) + letter;
		n = Math.floor((n - 1) / 26);
	}
	return letter;
};

// Convierte una celda de fecha a 'YYYY-MM-DD'. Acepta Date, número serial de Excel (cuando la celda
// está formateada como fecha) o texto en formato ISO / DD/MM/YYYY, ya que el lector XLSX compartido
// en CarguesMasivosWrapper.tsx no fuerza cellDates y no queremos tocar ese lector (lo usan Empresas/Vacantes).
const esFechaCalendarioValida = (y: number, m: number, d: number): boolean => {
	const f = new Date(Date.UTC(y, m - 1, d));
	return y >= 1900 && f.getUTCFullYear() === y && f.getUTCMonth() === m - 1 && f.getUTCDate() === d;
};

const parseFechaCelda = (valor: any): string | null => {
	const iso = parseFechaCeldaSinValidar(valor);
	if (!iso) return null;
	const [y, m, d] = iso.split('-').map(Number);
	return esFechaCalendarioValida(y, m, d) ? iso : null;
};

const parseFechaCeldaSinValidar = (valor: any): string | null => {
	if (valor == null || String(valor).trim() === '') return null;
	if (valor instanceof Date && !isNaN(valor.getTime())) {
		return valor.toISOString().slice(0, 10);
	}
	if (typeof valor === 'number') {
		const fecha = new Date(Math.round((valor - 25569) * 86400 * 1000));
		return isNaN(fecha.getTime()) ? null : fecha.toISOString().slice(0, 10);
	}
	const texto = String(valor).trim();
	const iso = texto.match(/^(\d{4})-(\d{2})-(\d{2})/);
	if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
	const ddmmyyyy = texto.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
	if (ddmmyyyy) {
		const [, d, m, y] = ddmmyyyy;
		return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
	}
	return null;
};

const PLANTILLA_CANDIDATOS_MAX_FILAS = 1000;
// Fila 1: encabezado, fila 2: obligatoriedad+tipo, fila 3: regla de validación, fila 4: primer dato.
const PLANTILLA_CANDIDATOS_FILA_DATOS_INICIO = 4;
const TEMPLATE_FILE_NAME = 'plantilla_candidatos.xlsx';
const TEMPLATE_SHEET_NAME = 'Candidatos';
// El sistema debe soportar hasta 10.000 registros por carga; los archivos que superen ese número se
// rechazan de entrada, antes de correr cualquier validación de negocio fila por fila.
const MAX_REGISTROS_CARGUE = 10000;

// Descarga un blob generado en memoria simulando un click en un <a download>.
const descargarBlob = (buffer: ExcelJS.Buffer, fileName: string) => {
	const blob = new Blob([buffer], {
		type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
	});
	const url = URL.createObjectURL(blob);
	const link = document.createElement('a');
	link.href = url;
	link.download = fileName;
	document.body.appendChild(link);
	link.click();
	link.remove();
	URL.revokeObjectURL(url);
};

interface ProcesarArchivoSubidoResultado {
	faltantes: string[];
	data: any[];
	hayErrores: boolean;
	excedeMaximo?: boolean;
	totalRegistros?: number;
}

interface ResumenCargue {
	id: number;
	fechaCargue: string;
	nombreArchivo: string;
	registrosProcesados: number;
	registrosConErrores: number;
	registrosInsertados: number;
}

interface ProcesarResultado {
	titulo: string;
	mensaje: string;
	nuevasFilas: any[] | null;
	resumen?: ResumenCargue;
}

export const useCargueCandidatos = () => {
	// Catálogos planos de `parameters` (uno por cada `tipo` referenciado en camposEnParametros),
	// indexados por tipo. Reemplaza los antiguos estados individuales (tiposDocumentoUsuarios, etc.)
	// ahora que son ~25 catálogos en vez de 2.
	const [catalogosPorTipo, setCatalogosPorTipo] = useState<Record<string, ParameterConfigMmodel[]>>({});
	// Municipios por departamento (código de departamento -> lista), para Municipio de nacimiento/residencia,
	// que son en cascada y por eso no llevan dropdown nativo (se validan como texto libre contra este set).
	const [municipiosPorDepartamento, setMunicipiosPorDepartamento] = useState<Record<string, ParameterConfigMmodel[]>>({});
	// Localidades de Bogotá (catálogo propio, no es un `parameters`), para la columna Localidad.
	const [localidades, setLocalidades] = useState<{ id: number; nombre: string }[]>([]);
	// Barrios por localidad (en cascada, por eso texto libre), para la columna Barrio.
	const [barriosPorLocalidad, setBarriosPorLocalidad] = useState<Record<string, { id: number; nombre: string }[]>>({});
	// Catálogo de cargos (búsqueda libre, igual que el Autocomplete de Step4.tsx), para "¿En qué le gustaría trabajar?".
	const [cargosCatalogo, setCargosCatalogo] = useState<{ id: number; desc_cargo: string }[]>([]);
	// Steps 5-8: catálogos propios (no `parameters`), ver getNivelesEducativos/getNucleosConocimiento/etc.
	const [nivelesEducativos, setNivelesEducativos] = useState<NivelEducativoOpcion[]>([]);
	const [nucleosConocimiento, setNucleosConocimiento] = useState<CatalogoIdDesc[]>([]);
	const [nivelesNucleos, setNivelesNucleos] = useState<NivelNucleoRelacion[]>([]);
	const [titulosHomologados, setTitulosHomologados] = useState<TituloHomologadoOpcion[]>([]);
	const [areasDesempeno, setAreasDesempeno] = useState<CatalogoIdDesc[]>([]);
	const [cargandoParametros, setCargandoParametros] = useState(true);
	const rawCandidatosRowsRef = useRef<any[] | null>(null);

	useEffect(() => {
		const cargarTodo = async () => {
			setCargandoParametros(true);
			try {
				const tiposUnicos = Array.from(new Set(candidatosCargue.camposEnParametros.map((c) => c.tipo)));
				const entradasCatalogos = await Promise.all(
					tiposUnicos.map(async (tipo) => [tipo, (await parameterSingleton.fetchData(tipo)) ?? []] as const)
				);
				const catalogos = Object.fromEntries(entradasCatalogos);
				setCatalogosPorTipo(catalogos);

				const departamentos: ParameterConfigMmodel[] = catalogos[parameters.DEPARTAMENTO] ?? [];
				const entradasMunicipios = await Promise.all(
					departamentos.map(async (dep) => [dep.code, (await parameterSingleton.fetchMunicipioById(dep.code)) ?? []] as const)
				);
				setMunicipiosPorDepartamento(Object.fromEntries(entradasMunicipios));

				const localidadesRaw = (await parameterSingleton.fetchLocalidades()) ?? [];
				const localidadesMapeadas = (localidadesRaw as any[]).map((l) => ({
					id: Number(l.id_localidad),
					nombre: String(l.desc_localidad ?? ''),
				}));
				setLocalidades(localidadesMapeadas);

				const entradasBarrios = await Promise.all(
					localidadesMapeadas.map(async (loc) => {
						const respuesta: any = await getBarriosByLocalidad(loc.id);
						const items = (respuesta?.data ?? []) as any[];
						return [String(loc.id), items.map((b) => ({ id: Number(b.id_barrio), nombre: String(b.nombre ?? '') }))] as const;
					})
				);
				setBarriosPorLocalidad(Object.fromEntries(entradasBarrios));

				const cargosRespuesta: any = await getCargos();
				setCargosCatalogo((cargosRespuesta?.data ?? []) as { id: number; desc_cargo: string }[]);

				const [nivelesResp, nucleosResp, nivelesNucleosResp, titulosResp, areasResp]: any[] = await Promise.all([
					getNivelesEducativos(),
					getNucleosConocimiento(),
					getNivelesNucleos(),
					getTitulosHomologados(),
					getAreasDesempenio(),
				]);
				setNivelesEducativos(
					((nivelesResp?.data ?? []) as any[]).map((n) => ({
						id: Number(n.id),
						sigla: String(n.sigla_nivel_educativo ?? ''),
						desc: String(n.desc_nivel_educativo ?? ''),
					}))
				);
				setNucleosConocimiento(
					((nucleosResp?.data ?? []) as any[]).map((n) => ({ id: Number(n.id), desc: String(n.desc_nucleo_conocimiento ?? '') }))
				);
				setNivelesNucleos(
					((nivelesNucleosResp?.data ?? []) as any[]).map((n) => ({
						id: Number(n.id),
						idNivelEducativo: Number(n.id_nivel_educativo),
						idNucleoConocimiento: Number(n.id_nucleo_conocimento),
					}))
				);
				setTitulosHomologados(
					((titulosResp?.data ?? []) as any[]).map((t) => ({
						id: Number(t.id),
						idNivelNucleo: Number(t.id_nivel_nucleo),
						desc: String(t.desc_titulo_homologado ?? ''),
					}))
				);
				setAreasDesempeno(
					((areasResp?.data ?? []) as any[]).map((a) => ({ id: Number(a.id), desc: String(a.desc_area_desempeno ?? '') }))
				);
			} catch (err) {
				console.error('Error trayendo catálogos del cargue de Candidatos:', err);
			} finally {
				setCargandoParametros(false);
			}
		};
		cargarTodo();
	}, []);

	const getListaCatalogo = (tipo: string): string[] => (catalogosPorTipo[tipo] ?? []).map((item) => item.value);

	const codigoDe = (tipo: string, valor: any): string | undefined => resolveCodeByValue(catalogosPorTipo[tipo] ?? [], valor);

	// Reconstruye "Dirección de residencia" a partir de las columnas de dirección descompuesta,
	// replicando handleDirectionValue() de steps/curriculum-registration/Step2.tsx (concatena CÓDIGOs
	// de catálogo, no las etiquetas, igual que hace la UI del candidato).
	const construirDireccionEstructurada = (row: any): string => {
		const partes = CamposDireccionResidencia.map(({ campo }) => {
			const valor = row[campo];
			if (valor == null || String(valor).trim() === '') return '';
			const tipoParam = candidatosCargue.camposEnParametros.find((c) => c.campo === campo)?.tipo;
			if (!tipoParam) return String(valor).trim(); // Número 1/2/3 (Dirección): texto libre, no catálogo
			return codigoDe(tipoParam, valor) ?? '';
		});
		let direccion = partes.join(' ').replace(/\s+/g, ' ').trim();

		const tipoComplementoCod = codigoDe(parameters.VIAPRINCIPALDIRECCION, row['Tipo de complemento (Dirección)']);
		const complementoTexto = String(row['Complemento (Dirección)'] ?? '').trim();
		if (tipoComplementoCod || complementoTexto) {
			direccion = `${direccion}, ${[tipoComplementoCod, complementoTexto].filter(Boolean).join(' ')}`.trim();
		}
		return direccion;
	};

	// Resuelve TODA la información derivada de una fila cruda del Excel (códigos de catálogo,
	// dirección final, cargo, etc.). La usan tanto la validación como el armado del payload de envío,
	// para no calcular las mismas reglas dos veces con resultados potencialmente distintos.
	const resolverFila = (row: any) => {
		const tipoDocCod = resolveCodeByValue(catalogosPorTipo[parameters.TIPODOCUMENTO] ?? [], row['Tipo de documento']);
		const sexoCod = codigoDe(parameters.SEXO, row['Sexo asignado al nacer']);
		const identidadGeneroCod = codigoDe(parameters.GENERO, row['Identidad de Género']);
		const paisNacCod = codigoDe(parameters.PAIS, row['País de nacimiento']);
		const nacionalidadCod = codigoDe(parameters.NACIONALIDAD, row['Nacionalidad']);
		const departamentoNacimientoCod = codigoDe(parameters.DEPARTAMENTO, row[CAMPO_DEPARTAMENTO_NACIMIENTO]);
		const municipioNacimientoLabel = row[CAMPO_MUNICIPIO_NACIMIENTO];
		const municipioNacimientoCod = departamentoNacimientoCod
			? resolveCodeByValue(municipiosPorDepartamento[departamentoNacimientoCod] ?? [], municipioNacimientoLabel)
			: undefined;
		const libretaCod = codigoDe(parameters.SINO, row['Libreta militar']);
		const tipoLibretaCod = codigoDe(parameters.TIPOLIBRETA, row['Tipo de libreta']);
		const cuentaSisbenCod = codigoDe(parameters.SINO, row['¿Cuenta con Sisbén?']);
		const tipoSisbenCod = codigoDe(parameters.SISBEN, row['Tipo de Sisbén']);
		const cuidadorCod = codigoDe(parameters.SINO, row['¿Usted es Cuidador o Cuidadora?']);
		const tipoCuidadorCod = codigoDe(parameters.PERSONACUIDADA, row['Persona que recibe cuidado']);
		const reconoceCod = codigoDe(parameters.SINO, row['¿Se reconoce como parte de una población con mayores barreras para conseguir empleo?']);
		const grupoEtnicoCod = codigoDe(parameters.GRUPOETNICO, row['Grupo étnico']);
		const personaDiscapCod = codigoDe(parameters.SINO, row['¿Es usted una persona con discapacidad?']);
		const condicionDiscapCod = codigoDe(parameters.DISCAPACIDADES, row['Tipo de discapacidad']);
		const tipoPoblacionCodigos =
			reconoceCod === CODIGO_SI
				? CamposTipoPoblacion.filter((c) => codigoDe(parameters.SINO, row[c.campo]) === CODIGO_SI).map((c) => c.codigoPoblacion)
				: [];
		const paisRetornadoCod = codigoDe(parameters.PAIS, row['País del cual fue retornado']);
		const paisResidenciaCod = codigoDe(parameters.PAIS, row['País de residencia']);
		const departamentoResidenciaCod = codigoDe(parameters.DEPARTAMENTO, row[CAMPO_DEPARTAMENTO_RESIDENCIA]);
		const municipioResidenciaLabel = row[CAMPO_MUNICIPIO_RESIDENCIA];
		const municipioResidenciaCod = departamentoResidenciaCod
			? resolveCodeByValue(municipiosPorDepartamento[departamentoResidenciaCod] ?? [], municipioResidenciaLabel)
			: undefined;
		const esBogota = municipioResidenciaCod === CODIGO_MUNICIPIO_BOGOTA;
		const localidadLabel = row[CAMPO_LOCALIDAD];
		const localidadEncontrada = localidadLabel
			? localidades.find((l) => l.nombre.trim().toLowerCase() === String(localidadLabel).trim().toLowerCase())
			: undefined;
		const barrioLabel = row[CAMPO_BARRIO];
		const listaBarrios = localidadEncontrada ? barriosPorLocalidad[String(localidadEncontrada.id)] ?? [] : [];
		const barrioEncontrado = barrioLabel
			? listaBarrios.find((b) => b.nombre.trim().toLowerCase() === String(barrioLabel).trim().toLowerCase())
			: undefined;
		const pertenceCod = codigoDe(parameters.ZONA, row['Pertenece a zona']);
		const prestadorPreferenciaCod = codigoDe(parameters.PRESTADORPREFERENCIA, row['Prestador de tu preferencia']);
		const puntoAtencionCod = codigoDe(parameters.PUNTOATENCION, row['Punto de atención']);
		const tipoComplementoCod = codigoDe(parameters.VIAPRINCIPALDIRECCION, row['Tipo de complemento (Dirección)']);
		const complementoTexto = String(row['Complemento (Dirección)'] ?? '').trim();
		const direccionEstructurada = construirDireccionEstructurada(row);
		const direccionResidencia = direccionEstructurada || String(row['Dirección de residencia'] ?? '').trim();
		const situacionLaboralCod = codigoDe(parameters.SITUACIONLABORAL, row['Situación laboral actual']);
		const possibilidadeViajarCod = codigoDe(parameters.SINO, row['Posibilidad de viajar']);
		const interesTeletrabajoCod = codigoDe(parameters.SINO, row['Interés en ofertas de teletrabajo']);
		const posibilidadTrasladarseCod = codigoDe(parameters.SINO, row['Posibilidad de trasladarse']);
		const conduceCarroCod = codigoDe(parameters.SINO, row['Conduce carro']);
		const categoriaLicenciaCarroCod = codigoDe(parameters.LINCENCIACARRO, row['Categoría licencia para carro']);
		const conduceMotoCod = codigoDe(parameters.SINO, row['Conduce moto']);
		const categoriaLicenciaMotoCod = codigoDe(parameters.LINCENCIAMOTO, row['Categoría licencia para moto']);
		const propMedioTransporteCod = codigoDe(parameters.SINO, row['Propietario de su medio de transporte']);
		const ultimoSalarioCod = codigoDe(parameters.SALARIOMENSUAL, row['¿Cuánto es tu salario actual o último salario?']);
		const aspiracionSalarialCod = codigoDe(parameters.SALARIOMENSUAL, row['¿Cuál es su expectativa salarial en este momento?']);
		const aspiracionSalarialMinCod = codigoDe(parameters.SALARIOMENSUAL, row['¿Cuál sería el salario mínimo aceptado en su trabajo soñado?']);
		const cargoLabel = row[CAMPO_CARGO_DESEADO];
		const cargoEncontrado = cargoLabel
			? cargosCatalogo.find((c) => c.desc_cargo?.trim().toLowerCase() === String(cargoLabel).trim().toLowerCase())
			: undefined;
		const fechaNacimientoIso = parseFechaCelda(row['Fecha de nacimiento']);

		// ================= Steps 5-8 =================
		const tieneNivelEducativoCod = codigoDe(parameters.SINO, row['¿Tiene educación formal?']);
		const nivelEducativoMaxEncontrado = buscarPorTexto(nivelesEducativos, row[CAMPO_NIVEL_EDUCATIVO_MAX], 'desc');
		const nivelEducativoEncontrado = buscarPorTexto(nivelesEducativos, row[CAMPO_NIVEL_EDUCATIVO], 'desc');
		const nivelEducativoSigla = nivelEducativoEncontrado?.sigla;
		const nucleoConocimientoEncontrado = buscarPorTexto(nucleosConocimiento, row[CAMPO_NUCLEO_CONOCIMIENTO], 'desc');
		const areaDesempenoEncontrada = buscarPorTexto(areasDesempeno, row[CAMPO_AREA_DESEMPENO], 'desc');
		// Título homologado: cascada real nivel_educativo -> nivel_nucleo -> titulo_homologado. Si no hay
		// núcleo seleccionado (grupos que no lo piden) se acepta cualquier nivel_nucleo de ese nivel.
		const nivelNucleoIdsValidos = nivelEducativoEncontrado
			? nivelesNucleos
					.filter(
						(n) =>
							n.idNivelEducativo === nivelEducativoEncontrado.id &&
							(!nucleoConocimientoEncontrado || n.idNucleoConocimiento === nucleoConocimientoEncontrado.id)
					)
					.map((n) => n.id)
			: [];
		const tituloHomologadoLabel = row[CAMPO_TITULO_HOMOLOGADO];
		const tituloHomologadoEncontrado = tituloHomologadoLabel
			? titulosHomologados.find(
					(t) =>
						nivelNucleoIdsValidos.includes(t.idNivelNucleo) &&
						t.desc.trim().toLowerCase() === String(tituloHomologadoLabel).trim().toLowerCase()
			  )
			: undefined;
		const estadoDelCursoCod = codigoDe(parameters.ESTADOCURSO, row['Estado del curso']);
		const fechaFinalizacionIso = parseFechaCelda(row['Fecha de finalización']);
		const tieneTarjetaCod = codigoDe(parameters.SINO, row['¿Tiene tarjeta profesional?']);
		const fechaTarjProfIso = parseFechaCelda(row['Fecha de expedición de tarjeta profesional']);

		const tieneExperienciaCod = codigoDe(parameters.SINO, row['¿Tiene experiencia laboral?']);
		const tipoExperienciaCod = codigoDe(parameters.TIPOEXPERIENCIALABORAL, row['Tipo de experiencia laboral']);
		const sectorCod = codigoDe(parameters.SECTORINDUSTRIAL, row['Sector']);
		const paisEmpresaCod = codigoDe(parameters.PAIS, row['País (Experiencia laboral)']);
		const departamentoEmpresaCod = codigoDe(parameters.DEPARTAMENTO, row['Departamento (Experiencia laboral)']);
		const municipioEmpresaCod = departamentoEmpresaCod
			? resolveCodeByValue(municipiosPorDepartamento[departamentoEmpresaCod] ?? [], row[CAMPO_MUNICIPIO_EMPRESA])
			: undefined;
		const esEmpleoActualCod = codigoDe(parameters.SINO, row['¿Este es su empleo actual?']);
		const fechaIngresoIso = parseFechaCelda(row['Fecha de ingreso']);
		const fechaRetiroIso = parseFechaCelda(row['Fecha de retiro']);

		const tieneCapacitacionesCod = codigoDe(parameters.SINO, row['¿Tiene capacitaciones y certificaciones?']);
		const tipoCapacitacionCod = codigoDe(parameters.TIPOCAPACITACION, row['Tipo capacitación o certificación']);
		const paisCapacitacionCod = codigoDe(parameters.PAIS, row['País (Capacitaciones y certificaciones)']);
		const estadoCapacitacionCod = codigoDe(parameters.ESTADOCERTIFICADO, row['Estado (Capacitaciones y certificaciones)']);
		const fechaCertificacionIso = parseFechaCelda(row['Fecha de certificación']);

		const idIdiomaCod = codigoDe(parameters.IDIOMA, row['Agregar idioma']);
		const nivelIdiomaCod = codigoDe(parameters.NIVELIDIOMA, row['Nivel idioma']);
		const tipoHerramientaCod = codigoDe(parameters.TIPOCONOCIMIENTOS, row['Tipo']);

		return {
			tipoDocCod, sexoCod, identidadGeneroCod, paisNacCod, nacionalidadCod,
			departamentoNacimientoCod, municipioNacimientoCod,
			libretaCod, tipoLibretaCod, cuentaSisbenCod, tipoSisbenCod, cuidadorCod, tipoCuidadorCod,
			reconoceCod, grupoEtnicoCod, personaDiscapCod, condicionDiscapCod, tipoPoblacionCodigos, paisRetornadoCod,
			paisResidenciaCod, departamentoResidenciaCod, municipioResidenciaCod, esBogota,
			localidadEncontrada, barrioEncontrado, pertenceCod, prestadorPreferenciaCod, puntoAtencionCod,
			tipoComplementoCod, complementoTexto, direccionResidencia,
			situacionLaboralCod, possibilidadeViajarCod, interesTeletrabajoCod, posibilidadTrasladarseCod,
			conduceCarroCod, categoriaLicenciaCarroCod, conduceMotoCod, categoriaLicenciaMotoCod, propMedioTransporteCod,
			ultimoSalarioCod, aspiracionSalarialCod, aspiracionSalarialMinCod,
			cargoEncontrado, fechaNacimientoIso,
			tieneNivelEducativoCod, nivelEducativoMaxEncontrado, nivelEducativoEncontrado, nivelEducativoSigla,
			nucleoConocimientoEncontrado, areaDesempenoEncontrada, tituloHomologadoEncontrado,
			estadoDelCursoCod, fechaFinalizacionIso, tieneTarjetaCod, fechaTarjProfIso,
			tieneExperienciaCod, tipoExperienciaCod, sectorCod, paisEmpresaCod, departamentoEmpresaCod,
			municipioEmpresaCod, esEmpleoActualCod, fechaIngresoIso, fechaRetiroIso,
			tieneCapacitacionesCod, tipoCapacitacionCod, paisCapacitacionCod, estadoCapacitacionCod, fechaCertificacionIso,
			idIdiomaCod, nivelIdiomaCod, tipoHerramientaCod,
		};
	};

	// Genera la plantilla de Candidatos con dropdowns nativos de Excel (validación de datos) para todos
	// los catálogos planos, alimentados desde columnas OCULTAS al final de la MISMA hoja (una sola hoja
	// en todo el libro — ya no se usa una segunda hoja "Listas"). Fila 1: encabezado. Fila 2:
	// obligatoriedad + tipo de dato. Fila 3: regla de validación. Fila 4 en adelante: datos.
	const descargarPlantilla = async () => {
		const workbook = new ExcelJS.Workbook();
		const headers = candidatosCargue.Headers;
		const worksheet = workbook.addWorksheet(TEMPLATE_SHEET_NAME);
		worksheet.columns = headers.map((header) => ({ header, key: header, width: 28 }));

		const CAMPOS_CATALOGO_ESPECIALES = new Set([
			CAMPO_LOCALIDAD, CAMPO_MUNICIPIO_NACIMIENTO, CAMPO_MUNICIPIO_RESIDENCIA, CAMPO_BARRIO, CAMPO_CARGO_DESEADO,
			CAMPO_NIVEL_EDUCATIVO_MAX, CAMPO_NIVEL_EDUCATIVO, CAMPO_NUCLEO_CONOCIMIENTO, CAMPO_AREA_DESEMPENO,
			CAMPO_TITULO_HOMOLOGADO, CAMPO_MUNICIPIO_EMPRESA,
		]);
		const esObligatorio = (campo: string) =>
			candidatosCargue.CamposObligatorios.includes(campo) || candidatosCargue.CamposObligatoriosCondicionales.includes(campo);
		const etiquetaTipo = (campo: string) => candidatosCargue.TiposDato[campo] ?? 'Texto';
		const filaTipoDato = headers.map((campo) => `[${esObligatorio(campo) ? 'Obligatorio' : 'Opcional'}] ${etiquetaTipo(campo)}`);
		const filaDescripcion = headers.map((campo) => candidatosCargue.Descripciones[campo] ?? '');
		worksheet.insertRow(2, filaTipoDato);
		worksheet.insertRow(3, filaDescripcion);
		worksheet.views = [{ state: 'frozen', ySplit: 3 }];

		const headerRow = worksheet.getRow(1);
		headerRow.height = 30;
		headerRow.eachCell((cell) => {
			cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
			cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1F497D' } };
			cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
		});

		const tipoDatoRow = worksheet.getRow(2);
		tipoDatoRow.eachCell((cell, colNumber) => {
			const obligatorio = esObligatorio(headers[colNumber - 1]);
			cell.font = { bold: true, color: { argb: obligatorio ? 'FFC55A11' : 'FF548235' } };
			cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: obligatorio ? 'FFFCE4D6' : 'FFE2EFDA' } };
			cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
		});

		const descripcionRow = worksheet.getRow(3);
		descripcionRow.height = 45;
		descripcionRow.eachCell((cell) => {
			cell.font = { italic: true, size: 9, color: { argb: 'FF808080' } };
			cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
		});

		const primeraFilaDatos = PLANTILLA_CANDIDATOS_FILA_DATOS_INICIO;
		const ultimaFila = PLANTILLA_CANDIDATOS_MAX_FILAS + primeraFilaDatos - 1;

		// Las tipificaciones de exceljs 4.4.0 no declaran "dataValidations" en Worksheet, aunque la API
		// sí existe en runtime (verificado generando el archivo real) — no quitar el cast.
		const aplicarDropdownMismaHoja = (colDestinoLetra: string, colListaLetra: string, cantidadValores: number) => {
			(worksheet as unknown as { dataValidations: { add: (address: string, validation: unknown) => void } }).dataValidations.add(
				`${colDestinoLetra}${primeraFilaDatos}:${colDestinoLetra}${ultimaFila}`,
				{
					type: 'list',
					allowBlank: true,
					formulae: [`$${colListaLetra}$2:$${colListaLetra}$${cantidadValores + 1}`],
					showErrorMessage: true,
					errorStyle: 'stop',
					errorTitle: 'Valor no válido',
					error: 'Seleccione un valor de la lista desplegable.',
				}
			);
		};

		// Columnas de referencia ocultas, lejos de los datos visibles pero en la MISMA hoja.
		// El encabezado (fila 1) de estas columnas NO puede repetir el texto de la columna visible que
		// alimentan: al releer el archivo subido, sheet_to_json arma un objeto por fila usando la fila 1
		// como claves, y si dos columnas comparten encabezado la última pisa el valor de la primera —
		// eso hacía que la columna oculta (con un valor de catálogo cualquiera, no el que la persona
		// digitó) sobrescribiera el valor real diligenciado por la persona. Por eso se prefija.
		let columnaListaActual = headers.length + 3;
		const escribirColumnaListaOculta = (header: string, valores: string[]): string => {
			const colIndex = columnaListaActual++;
			const columna = worksheet.getColumn(colIndex);
			columna.values = [`__lista__${header}`, ...valores];
			columna.hidden = true;
			return columnLetter(colIndex);
		};

		// Un dropdown por cada columna-catálogo (sin duplicar cuando varias columnas comparten `tipo`,
		// ej. las 3 columnas "Letra N (Dirección)" todas usan VIAPRINCIPALPRIMERALETRA).
		candidatosCargue.camposEnParametros.forEach(({ campo, tipo }) => {
			const filtro = FILTROS_VALORES_DROPDOWN[campo];
			const itemsCatalogo = catalogosPorTipo[tipo] ?? [];
			const valores = (filtro ? itemsCatalogo.filter(filtro) : itemsCatalogo).map((item) => item.value);
			if (valores.length === 0) return; // aún no cargan los parámetros, se omite el dropdown
			const colListaLetra = escribirColumnaListaOculta(campo, valores);
			const colDestinoLetra = columnLetter(headers.indexOf(campo) + 1);
			aplicarDropdownMismaHoja(colDestinoLetra, colListaLetra, valores.length);
		});

		// Localidad: catálogo propio (no viene de `parameters`), mismo mecanismo de dropdown.
		if (localidades.length > 0 && headers.includes(CAMPO_LOCALIDAD)) {
			const valores = localidades.map((l) => l.nombre);
			const colListaLetra = escribirColumnaListaOculta(CAMPO_LOCALIDAD, valores);
			const colDestinoLetra = columnLetter(headers.indexOf(CAMPO_LOCALIDAD) + 1);
			aplicarDropdownMismaHoja(colDestinoLetra, colListaLetra, valores.length);
		}

		// Municipio de nacimiento/residencia: SÍ son catálogo paramétrico (parámetro MUNICIPIO), no
		// texto libre — solo que el sistema los sirve en cascada por departamento. Para el dropdown se
		// ofrece el listado nacional completo (deduplicado); la validación real (ver resolverFila) sigue
		// exigiendo que el municipio elegido exista dentro del departamento indicado en esa misma fila.
		const todosMunicipios = Array.from(
			new Set(Object.values(municipiosPorDepartamento).flat().map((m) => m.value))
		).sort((a, b) => a.localeCompare(b, 'es'));
		if (todosMunicipios.length > 0) {
			[CAMPO_MUNICIPIO_NACIMIENTO, CAMPO_MUNICIPIO_RESIDENCIA, CAMPO_MUNICIPIO_EMPRESA].forEach((campoMunicipio) => {
				if (!headers.includes(campoMunicipio)) return;
				const colListaLetra = escribirColumnaListaOculta(campoMunicipio, todosMunicipios);
				const colDestinoLetra = columnLetter(headers.indexOf(campoMunicipio) + 1);
				aplicarDropdownMismaHoja(colDestinoLetra, colListaLetra, todosMunicipios.length);
			});
		}

		// Barrio: mismo caso que Municipio (catálogo real servido en cascada por localidad).
		const todosBarrios = Array.from(
			new Set(Object.values(barriosPorLocalidad).flat().map((b) => b.nombre))
		).sort((a, b) => a.localeCompare(b, 'es'));
		if (todosBarrios.length > 0 && headers.includes(CAMPO_BARRIO)) {
			const colListaLetra = escribirColumnaListaOculta(CAMPO_BARRIO, todosBarrios);
			const colDestinoLetra = columnLetter(headers.indexOf(CAMPO_BARRIO) + 1);
			aplicarDropdownMismaHoja(colDestinoLetra, colListaLetra, todosBarrios.length);
		}

		// Cargo deseado: catálogo real de Cargos (en la UI del candidato es un buscador/Autocomplete
		// contra este mismo catálogo, ver Step4.tsx), no texto libre.
		const todosCargos = Array.from(new Set(cargosCatalogo.map((c) => c.desc_cargo).filter(Boolean)))
			.sort((a, b) => a.localeCompare(b, 'es'));
		if (todosCargos.length > 0 && headers.includes(CAMPO_CARGO_DESEADO)) {
			const colListaLetra = escribirColumnaListaOculta(CAMPO_CARGO_DESEADO, todosCargos);
			const colDestinoLetra = columnLetter(headers.indexOf(CAMPO_CARGO_DESEADO) + 1);
			aplicarDropdownMismaHoja(colDestinoLetra, colListaLetra, todosCargos.length);
		}

		// Nivel educativo (máximo y del registro puntual): mismo catálogo (endpoint propio, no `parameters`).
		const nombresNivelesEducativos = Array.from(new Set(nivelesEducativos.map((n) => n.desc).filter(Boolean)));
		if (nombresNivelesEducativos.length > 0) {
			[CAMPO_NIVEL_EDUCATIVO_MAX, CAMPO_NIVEL_EDUCATIVO].forEach((campo) => {
				if (!headers.includes(campo)) return;
				const colListaLetra = escribirColumnaListaOculta(campo, nombresNivelesEducativos);
				const colDestinoLetra = columnLetter(headers.indexOf(campo) + 1);
				aplicarDropdownMismaHoja(colDestinoLetra, colListaLetra, nombresNivelesEducativos.length);
			});
		}

		// Núcleo de conocimiento (endpoint propio).
		const nombresNucleos = Array.from(new Set(nucleosConocimiento.map((n) => n.desc).filter(Boolean)));
		if (nombresNucleos.length > 0 && headers.includes(CAMPO_NUCLEO_CONOCIMIENTO)) {
			const colListaLetra = escribirColumnaListaOculta(CAMPO_NUCLEO_CONOCIMIENTO, nombresNucleos);
			const colDestinoLetra = columnLetter(headers.indexOf(CAMPO_NUCLEO_CONOCIMIENTO) + 1);
			aplicarDropdownMismaHoja(colDestinoLetra, colListaLetra, nombresNucleos.length);
		}

		// Área de desempeño (endpoint propio).
		const nombresAreas = Array.from(new Set(areasDesempeno.map((a) => a.desc).filter(Boolean)));
		if (nombresAreas.length > 0 && headers.includes(CAMPO_AREA_DESEMPENO)) {
			const colListaLetra = escribirColumnaListaOculta(CAMPO_AREA_DESEMPENO, nombresAreas);
			const colDestinoLetra = columnLetter(headers.indexOf(CAMPO_AREA_DESEMPENO) + 1);
			aplicarDropdownMismaHoja(colDestinoLetra, colListaLetra, nombresAreas.length);
		}

		// Título homologado: catálogo real (endpoint propio) aunque con ~10.900 registros — se ofrece el
		// listado completo deduplicado; la validación real (ver resolverFila) exige que el título elegido
		// corresponda al Nivel educativo (y Núcleo de conocimiento, cuando aplique) de esa misma fila.
		const nombresTitulos = Array.from(new Set(titulosHomologados.map((t) => t.desc).filter(Boolean)));
		if (nombresTitulos.length > 0 && headers.includes(CAMPO_TITULO_HOMOLOGADO)) {
			const colListaLetra = escribirColumnaListaOculta(CAMPO_TITULO_HOMOLOGADO, nombresTitulos);
			const colDestinoLetra = columnLetter(headers.indexOf(CAMPO_TITULO_HOMOLOGADO) + 1);
			aplicarDropdownMismaHoja(colDestinoLetra, colListaLetra, nombresTitulos.length);
		}

		const buffer = await workbook.xlsx.writeBuffer();
		descargarBlob(buffer, TEMPLATE_FILE_NAME);
	};

	const validarEncabezado = (headers: string[]): string[] => {
		const requiredHeaders = candidatosCargue.Headers;
		return requiredHeaders.filter((h) => !headers.includes(h));
	};

	/* Validaciones para el candidato, replicando las reglas de negocio de "Datos del usuario" y de
	 * los Steps 1-4 de CurriculumRegistration.tsx (handleValidOnSubmit de Step1/Step2/Step3.tsx). */
	const validarCandidatoCarga = (rawData: any[], filaInicio: number): { datos: any[]; hayErrores: boolean } => {
		let hayErrores = false;

		// Precalcular repeticiones de campos únicos en el archivo
		const repeticiones: Record<string, Map<string, number[]>> = {};
		candidatosCargue.CamposUnicos.forEach((campo: string) => {
			const mapa = new Map<string, number[]>();
			rawData.forEach((row: any, index: number) => {
				const valor = row[campo];
				if (valor != null && String(valor).trim() !== "") {
					const clave = String(valor).trim().toLowerCase();
					const filas = mapa.get(clave) || [];
					filas.push(index + filaInicio);
					mapa.set(clave, filas);
				}
			});
			repeticiones[campo] = mapa;
		});

		// Duplicidad por combinación Tipo de documento + Número de documento dentro del archivo: si
		// existe, los registros involucrados se marcan como duplicados (regla de negocio explícita,
		// independiente del chequeo genérico de CamposUnicos de arriba).
		const combosDocumento = new Map<string, number[]>();
		rawData.forEach((row: any, index: number) => {
			const tipoDoc = row['Tipo de documento'];
			const numeroDoc = row['Número de documento'];
			if (tipoDoc != null && String(tipoDoc).trim() !== "" && numeroDoc != null && String(numeroDoc).trim() !== "") {
				const clave = `${String(tipoDoc).trim().toLowerCase()}|${String(numeroDoc).trim().toLowerCase()}`;
				const filas = combosDocumento.get(clave) || [];
				filas.push(index + filaInicio);
				combosDocumento.set(clave, filas);
			}
		});

		const camposNombre = [
			{ campo: 'Primer nombre', requerido: true },
			{ campo: 'Segundo nombre', requerido: false },
			{ campo: 'Primer apellido', requerido: true },
			{ campo: 'Segundo apellido', requerido: false },
		];

		const datosConVacias = rawData.map((row: any, index: number) => {
			// Fila fantasma (ver esFilaVacia): se descarta, no se muestra ni como OK ni como ERROR.
			if (esFilaVacia(row)) return null;

			let observaciones: string[] = [];
			const requerirSi = (condicion: boolean, campo: string) => {
				if (!condicion) return;
				const valor = row[campo];
				if (valor == null || String(valor).trim() === "") {
					observaciones.push(`<b>${campo}</b> es obligatorio`);
				}
			};

			// ********** Validar campos obligatorios (incondicionales) *************
			candidatosCargue.CamposObligatorios.forEach((campo: string) => {
				const valor = row[campo];
				if (valor == null || String(valor).trim() == "") {
					observaciones.push(`<b>${campo}</b> es obligatorio`);
				}
			});

			// ********** Validar cantidad de caracteres **************
			candidatosCargue.LimitesCaracteres?.forEach(({ campo, max, min }: any) => {
				const valor = row[campo];
				if (valor != null && String(valor).trim() !== "") {
					const texto = String(valor).trim();
					if (max && texto.length > max) {
						observaciones.push(`<b>${campo}</b>: Valor supera el máximo de ${max} caracteres (tiene ${texto.length})`);
					}
					if (min && texto.length < min) {
						observaciones.push(`<b>${campo}</b>: Valor debe tener al menos ${min} caracteres (tiene ${texto.length})`);
					}
				}
			});

			// ********** Validar valores repetidos en el archivo **************
			candidatosCargue.CamposUnicos.forEach((campo: string) => {
				const valor = row[campo];
				if (valor != null && String(valor).trim() !== "") {
					const clave = String(valor).trim().toLowerCase();
					const filas = repeticiones[campo]?.get(clave);
					if (filas && filas.length > 1) {
						observaciones.push(`<b>${campo}</b>: Valor '${valor}' está repetido en el archivo`);
					}
				}
			});

			// ********** Validar duplicidad por Tipo + Número de documento **************
			{
				const tipoDoc = row['Tipo de documento'];
				const numeroDoc = row['Número de documento'];
				if (tipoDoc != null && String(tipoDoc).trim() !== "" && numeroDoc != null && String(numeroDoc).trim() !== "") {
					const clave = `${String(tipoDoc).trim().toLowerCase()}|${String(numeroDoc).trim().toLowerCase()}`;
					const filas = combosDocumento.get(clave);
					if (filas && filas.length > 1) {
						observaciones.push(`<b>Tipo de documento</b> y <b>Número de documento</b>: esta combinación está duplicada en el archivo`);
					}
				}
			}

			// ********* Validar campos de tipo lista (selects) *************
			candidatosCargue.camposEnParametros.forEach(({ campo, tipo }) => {
				const valor = row[campo];
				if (valor != null && String(valor).trim() !== "") {
					const listaParametros = getListaCatalogo(tipo) || [];
					const valorTexto = String(valor).trim().toLowerCase();
					const existe = listaParametros.some((x: any) => String(x).trim().toLowerCase() == valorTexto);
					if (!existe) {
						observaciones.push(`<b>${campo}</b>: El valor '${valor}' no es válido`);
					}
				}
			});

			// ********* Validar nombres y apellidos *************
			camposNombre.forEach(({ campo, requerido }) => {
				const valor = row[campo];
				if (valor != null && String(valor).trim() !== "") {
					if (!isValidFirstSecondNameFirstSecondLastName(String(valor).trim(), requerido)) {
						observaciones.push(`<b>${campo}</b>: Valor no válido`);
					}
				}
			});

			// ********* Validar correo electrónico *************
			const correo = row['Correo electrónico'];
			if (correo != null && String(correo).trim() !== "" && !isValidEmail(String(correo).trim())) {
				observaciones.push(`<b>Correo electrónico</b>: No es un correo electrónico válido`);
			}

			// ********* Validar teléfono *************
			const telefono = row['Teléfono'];
			if (telefono != null && String(telefono).trim() !== "") {
				const telefonoLimpio = String(telefono).replace(/\D/g, '');
				if (!isValidColombianPhoneNumber(telefonoLimpio, true)) {
					observaciones.push(`<b>Teléfono</b>: Número telefónico no válido`);
				}
			}

			// ********* Validar otro teléfono (opcional, mismo formato que Teléfono) *************
			const otroTelefono = row['Otro teléfono'];
			if (otroTelefono != null && String(otroTelefono).trim() !== "") {
				if (!isValidColombianPhoneNumber(String(otroTelefono).replace(/\D/g, ''), false)) {
					observaciones.push(`<b>Otro teléfono</b>: Número telefónico no válido`);
				}
			}

			// ********* Validar número de documento según el tipo de documento *************
			const tipoDocumentoLabel = row['Tipo de documento'];
			const numeroDocumento = row['Número de documento'];
			const r = resolverFila(row);
			if (
				tipoDocumentoLabel != null && String(tipoDocumentoLabel).trim() !== "" &&
				numeroDocumento != null && String(numeroDocumento).trim() !== ""
			) {
				const regla = r.tipoDocCod ? DOCUMENT_TYPE_RULES[r.tipoDocCod] : undefined;
				if (regla) {
					const valido = isValidDocumentNumber(
						String(numeroDocumento).trim(),
						regla.type,
						r.tipoDocCod as string,
						regla.min,
						regla.max,
						true
					);
					if (!valido) {
						observaciones.push(`<b>Número de documento</b>: No es válido para el tipo de documento seleccionado`);
					}
				}
			}

			// ================= Steps 1-4 de CurriculumRegistration.tsx =================

			// Fecha de nacimiento: formato válido, no futura, y reglas cruzadas con Tipo de documento.
			const fechaNacRaw = row['Fecha de nacimiento'];
			if (fechaNacRaw != null && String(fechaNacRaw).trim() !== "") {
				if (!r.fechaNacimientoIso) {
					observaciones.push(`<b>Fecha de nacimiento</b>: Fecha no válida`);
				} else {
					if (new Date(r.fechaNacimientoIso) > new Date()) {
						observaciones.push(`<b>Fecha de nacimiento</b>: No debe ser mayor a la fecha actual`);
					}
					checkRulesByBirthDateAndDocType(r.fechaNacimientoIso, r.tipoDocCod).forEach((e: any) =>
						observaciones.push(`<b>${e.field}</b>: ${e.error}`)
					);
				}
			}

			// País de nacimiento <-> Tipo de documento (PEP/PT no pueden ser colombianos)
			if (r.paisNacCod && r.tipoDocCod) {
				checkRulesByCountryAndDocumentType(r.paisNacCod, r.tipoDocCod).forEach((e: any) =>
					observaciones.push(`<b>${e.field}</b>: ${e.error}`)
				);
			}

			// Nacionalidad debe corresponder al país de nacimiento (mismo criterio que Step1.tsx: el
			// código de nacionalidad contiene el código de país).
			if (row['Nacionalidad'] && r.paisNacCod && r.nacionalidadCod && !String(r.nacionalidadCod).includes(String(r.paisNacCod))) {
				observaciones.push(`<b>Nacionalidad</b>: Debe corresponder al país de nacimiento indicado`);
			}

			// Departamento/Municipio de nacimiento: obligatorios solo si el país de nacimiento es Colombia.
			requerirSi(r.paisNacCod === CODIGO_PAIS_COLOMBIA, CAMPO_DEPARTAMENTO_NACIMIENTO);
			requerirSi(r.paisNacCod === CODIGO_PAIS_COLOMBIA, CAMPO_MUNICIPIO_NACIMIENTO);
			if (row[CAMPO_MUNICIPIO_NACIMIENTO] && r.departamentoNacimientoCod && !r.municipioNacimientoCod) {
				observaciones.push(`<b>${CAMPO_MUNICIPIO_NACIMIENTO}</b>: No es un municipio válido para el departamento indicado`);
			}

			// Libreta militar (solo hombres); Número de libreta obligatorio y de 6 a 10 dígitos si Libreta
			// militar es Sí (Tipo de libreta queda opcional según la spec, aunque se habilite el campo).
			requerirSi(r.sexoCod === CODIGO_SEXO_MASCULINO, 'Libreta militar');
			requerirSi(r.libretaCod === CODIGO_SI, 'Número de libreta');
			const numeroLibreta = row['Número de libreta'];
			if (numeroLibreta != null && String(numeroLibreta).trim() !== '' && !/^\d{6,10}$/.test(String(numeroLibreta).trim())) {
				observaciones.push(`<b>Número de libreta</b>: Debe tener entre 6 y 10 dígitos`);
			}

			// Cuidador (Sisbén y Tipo de discapacidad quedan opcionales según la spec).
			requerirSi(r.cuidadorCod === CODIGO_SI, 'Persona que recibe cuidado');

			// Población con mayores barreras: si la pregunta principal es Sí, cada columna de tipo de
			// población debe quedar respondida (Sí/No) y al menos una debe ser Sí.
			requerirSi(r.reconoceCod === CODIGO_SI, 'Grupo étnico');
			requerirSi(r.reconoceCod === CODIGO_SI, '¿Es usted una persona con discapacidad?');
			requerirSi(r.personaDiscapCod === CODIGO_SI, 'Tipo de discapacidad');
			if (r.reconoceCod === CODIGO_SI) {
				CamposTipoPoblacion.forEach((c) => requerirSi(true, c.campo));
				if (r.tipoPoblacionCodigos.length === 0) {
					observaciones.push(`<b>Tipo de población</b>: Debe marcar Sí en al menos una de las columnas de tipo de población`);
				}
			}

			// Residencia: País de residencia debe ser específicamente Colombia (no cualquier país del
			// catálogo PAIS), y Departamento debe ser Bogotá D.C. o Cundinamarca.
			if (row['País de residencia'] && r.paisResidenciaCod !== CODIGO_PAIS_COLOMBIA) {
				observaciones.push(`<b>País de residencia</b>: Debe ser Colombia`);
			}
			if (row[CAMPO_DEPARTAMENTO_RESIDENCIA] && r.departamentoResidenciaCod && !['11', '25'].includes(r.departamentoResidenciaCod)) {
				observaciones.push(`<b>${CAMPO_DEPARTAMENTO_RESIDENCIA}</b>: Debe ser Bogotá D.C. o Cundinamarca`);
			}
			if (row[CAMPO_MUNICIPIO_RESIDENCIA] && r.departamentoResidenciaCod && !r.municipioResidenciaCod) {
				observaciones.push(`<b>${CAMPO_MUNICIPIO_RESIDENCIA}</b>: No es un municipio válido para el departamento indicado`);
			}
			requerirSi(r.esBogota, CAMPO_LOCALIDAD);
			requerirSi(r.esBogota, CAMPO_BARRIO);
			requerirSi(r.esBogota, 'Punto de atención');
			if (row[CAMPO_LOCALIDAD] && !r.localidadEncontrada) {
				observaciones.push(`<b>${CAMPO_LOCALIDAD}</b>: No es una localidad válida`);
			}
			if (row[CAMPO_BARRIO] && !r.barrioEncontrado) {
				observaciones.push(`<b>${CAMPO_BARRIO}</b>: No es un barrio válido para la localidad indicada`);
			}

			// Números de dirección (1/2/3): deben ser numéricos y >= 0 cuando se diligencian.
			['Número 1 (Dirección)', 'Número 2 (Dirección)', 'Número 3 (Dirección)'].forEach((campo) => {
				const valor = row[campo];
				if (valor != null && String(valor).trim() !== '' && (!/^\d+$/.test(String(valor).trim()) || Number(valor) < 0)) {
					observaciones.push(`<b>${campo}</b>: Debe ser un número mayor o igual a 0`);
				}
			});

			// Complemento de dirección: "Complemento" obligatorio solo si se indicó "Tipo de complemento".
			if (row['Tipo de complemento (Dirección)'] && !r.complementoTexto) {
				observaciones.push(`<b>Complemento (Dirección)</b> es obligatorio`);
			}

			// Dirección de residencia: calculada de las columnas estructuradas o tomada tal cual.
			if (!r.direccionResidencia) {
				observaciones.push(`<b>Dirección de residencia</b> es obligatorio`);
			}

			// Número de tarjeta profesional: si se diligencia, solo dígitos (4 a 10).
			const numeroTarjeta = row['Número de tarjeta profesional'];
			if (numeroTarjeta != null && String(numeroTarjeta).trim() !== '' && !/^\d{4,10}$/.test(String(numeroTarjeta).trim())) {
				observaciones.push(`<b>Número de tarjeta profesional</b>: Debe tener entre 4 y 10 dígitos`);
			}

			// Licencias de conducción
			requerirSi(r.conduceCarroCod === CODIGO_SI, 'Categoría licencia para carro');
			requerirSi(r.conduceMotoCod === CODIGO_SI, 'Categoría licencia para moto');

			// Salarios: no admiten los códigos "0" ni "-1" (placeholders del catálogo SALARIOMENSUAL,
			// ver MenuItem deshabilitados en Step3.tsx), y el mínimo aceptado no puede superar la expectativa.
			[
				{ campo: '¿Cuánto es tu salario actual o último salario?', cod: r.ultimoSalarioCod },
				{ campo: '¿Cuál es su expectativa salarial en este momento?', cod: r.aspiracionSalarialCod },
				{ campo: '¿Cuál sería el salario mínimo aceptado en su trabajo soñado?', cod: r.aspiracionSalarialMinCod },
			].forEach(({ campo, cod }) => {
				if (row[campo] && (cod === '0' || cod === '-1')) {
					observaciones.push(`<b>${campo}</b>: No es un valor de salario válido`);
				}
			});
			if (
				row['¿Cuál sería el salario mínimo aceptado en su trabajo soñado?'] &&
				row['¿Cuál es su expectativa salarial en este momento?'] &&
				!salarioMinIsGreaterThanSalarioMax(r.aspiracionSalarialMinCod, r.aspiracionSalarialCod)
			) {
				observaciones.push(`<b>¿Cuál sería el salario mínimo aceptado en su trabajo soñado?</b>: No puede ser mayor a la expectativa salarial`);
			}

			// Cargo deseado: debe coincidir con el catálogo de cargos.
			if (row[CAMPO_CARGO_DESEADO] && !r.cargoEncontrado) {
				observaciones.push(`<b>${CAMPO_CARGO_DESEADO}</b>: No corresponde a un cargo válido del catálogo`);
			}

			// ================= Steps 5-8 =================

			// Fecha no futura: helper reutilizado por Nivel educativo/Experiencia/Capacitaciones.
			const noEsFutura = (iso: string | null) => !iso || new Date(iso) <= new Date();

			// Formato de fecha: si la celda tiene contenido pero no se pudo interpretar como una fecha real
			// (DD/MM/AAAA, AAAA-MM-DD o fecha de Excel), se reporta en vez de ignorarla en silencio.
			[
				{ campo: 'Fecha de finalización', iso: r.fechaFinalizacionIso },
				{ campo: 'Fecha de expedición de tarjeta profesional', iso: r.fechaTarjProfIso },
				{ campo: 'Fecha de ingreso', iso: r.fechaIngresoIso },
				{ campo: 'Fecha de retiro', iso: r.fechaRetiroIso },
				{ campo: 'Fecha de certificación', iso: r.fechaCertificacionIso },
			].forEach(({ campo, iso }) => {
				const crudo = row[campo];
				if (crudo != null && String(crudo).trim() !== '' && !iso) {
					observaciones.push(`<b>${campo}</b>: Fecha no válida, use el formato DD/MM/AAAA`);
				}
			});

			// --- Nivel educativo (Step5.tsx) --- obligatoriedad según la plantilla oficial (ver Descripciones
			// "Dependencia:" en listas.ts).
			const tieneNivelEducativo = r.tieneNivelEducativoCod === CODIGO_SI;
			requerirSi(tieneNivelEducativo, CAMPO_NIVEL_EDUCATIVO_MAX);
			requerirSi(tieneNivelEducativo, CAMPO_NIVEL_EDUCATIVO);
			requerirSi(tieneNivelEducativo, 'Título obtenido');
			requerirSi(tieneNivelEducativo, CAMPO_INSTITUCION_NIVEL_EDUCATIVO);
			if (row[CAMPO_NIVEL_EDUCATIVO_MAX] && !r.nivelEducativoMaxEncontrado) {
				observaciones.push(`<b>${CAMPO_NIVEL_EDUCATIVO_MAX}</b>: No es un nivel educativo válido`);
			}
			if (row[CAMPO_NIVEL_EDUCATIVO] && !r.nivelEducativoEncontrado) {
				observaciones.push(`<b>${CAMPO_NIVEL_EDUCATIVO}</b>: No es un nivel educativo válido`);
			}
			if (tieneNivelEducativo && r.nivelEducativoSigla) {
				const sigla = r.nivelEducativoSigla;
				const esSuperior = GRUPO_NIVEL_SUPERIOR.includes(sigla);
				const esGraduado = r.estadoDelCursoCod === CODIGO_ESTADO_CURSO_GRADUADO;
				requerirSi(r.estadoDelCursoCod === CODIGO_ESTADO_CURSO_GRADUADO, 'Fecha de finalización');
				if (!noEsFutura(r.fechaFinalizacionIso)) {
					observaciones.push(`<b>Fecha de finalización</b>: No debe ser mayor a la fecha actual`);
				}
				requerirSi(esSuperior, CAMPO_NUCLEO_CONOCIMIENTO);
				requerirSi(esGraduado && esSuperior, '¿Tiene tarjeta profesional?');
				requerirSi(r.tieneTarjetaCod === CODIGO_SI, 'Número de tarjeta profesional');

				if (!noEsFutura(r.fechaTarjProfIso)) {
					observaciones.push(`<b>Fecha de expedición de tarjeta profesional</b>: No debe ser mayor a la fecha actual`);
				}
				if (row[CAMPO_TITULO_HOMOLOGADO] && !r.tituloHomologadoEncontrado) {
					observaciones.push(`<b>${CAMPO_TITULO_HOMOLOGADO}</b>: No corresponde al Nivel educativo (y Núcleo de conocimiento, si aplica) indicados`);
				}
			}

			// --- Experiencia laboral (Step6.tsx) --- Cargo, Nombre de la empresa y las fechas de ingreso/retiro
			// son obligatorias cuando aplica (plantilla oficial).
			const tieneExperiencia = r.tieneExperienciaCod === CODIGO_SI;
			if (tieneExperiencia) {
				requerirSi(true, 'Cargo');
				requerirSi(true, 'Nombre de la empresa');
				requerirSi(true, 'Fecha de ingreso');
				requerirSi(r.esEmpleoActualCod === CODIGO_NO, 'Fecha de retiro');
			}
			if (!noEsFutura(r.fechaIngresoIso)) {
				observaciones.push(`<b>Fecha de ingreso</b>: No debe ser mayor a la fecha actual`);
			}
			if (!noEsFutura(r.fechaRetiroIso)) {
				observaciones.push(`<b>Fecha de retiro</b>: No debe ser mayor a la fecha actual`);
			}
			if (row['Departamento (Experiencia laboral)'] && row[CAMPO_MUNICIPIO_EMPRESA] && r.departamentoEmpresaCod && !r.municipioEmpresaCod) {
				observaciones.push(`<b>${CAMPO_MUNICIPIO_EMPRESA}</b>: No es un municipio válido para el departamento indicado`);
			}
			if (r.fechaIngresoIso && r.fechaRetiroIso && r.fechaRetiroIso < r.fechaIngresoIso) {
				observaciones.push(`<b>Fecha de retiro</b>: No puede ser anterior a la Fecha de ingreso`);
			}
			const telefonoEmpresa = row['Teléfono de la empresa'];
			if (telefonoEmpresa && !isValidColombianPhoneNumber(String(telefonoEmpresa).replace(/\D/g, ''), false)) {
				observaciones.push(`<b>Teléfono de la empresa</b>: Número telefónico no válido`);
			}

			// --- Capacitaciones y certificaciones (Step7.tsx) --- (todo opcional salvo Duración en
			// horas > 0 y Fecha de certificación no futura, cuando se diligencian)
			if (!noEsFutura(r.fechaCertificacionIso)) {
				observaciones.push(`<b>Fecha de certificación</b>: No debe ser mayor a la fecha actual`);
			}
			const duracionHoras = row['Duración en horas'];
			if (duracionHoras != null && String(duracionHoras).trim() !== '' && (Number.isNaN(Number(duracionHoras)) || Number(duracionHoras) <= 0)) {
				observaciones.push(`<b>Duración en horas</b>: Debe ser un número mayor a 0`);
			}

			// --- Idiomas y herramientas (Step8.tsx) --- (Agregar idioma, Nivel idioma, Tipo,
			// Herramientas y Nivel son cada uno independiente y opcional, sin exigencia cruzada entre ellos).
			const nivelHerramientaValor = row['Nivel'];
			if (nivelHerramientaValor != null && String(nivelHerramientaValor).trim() !== '') {
				const nivelNum = Number(nivelHerramientaValor);
				if (Number.isNaN(nivelNum) || !Number.isInteger(nivelNum) || nivelNum < 1 || nivelNum > 5) {
					observaciones.push(`<b>Nivel</b>: Debe ser un número entero entre 1 y 5`);
				}
			}

			if (observaciones.length > 0) hayErrores = true;

			return {
				"N° Fila": index + filaInicio,
				Estado: observaciones.length > 0 ? `<span class='text-danger'>ERROR</span>` : `<span class='text-success'>OK</span>`,
				Observación: observaciones.join(" <br> "),
			};
		});

		const datos = datosConVacias.filter((d): d is NonNullable<typeof d> => d !== null);

		return { datos, hayErrores };
	};

	const procesarArchivoSubido = (rawDataAll: any[]): ProcesarArchivoSubidoResultado => {
		const headers = Object.keys((rawDataAll[0] ?? {}) as object);
		const faltantes = validarEncabezado(headers);
		if (faltantes.length > 0) {
			return { faltantes, data: [], hayErrores: false };
		}

		const filaInicio = PLANTILLA_CANDIDATOS_FILA_DATOS_INICIO;
		const rawDataCandidato = rawDataAll.slice(filaInicio - 2) as any[];

		// Límite de 10.000 registros por carga: se valida antes de correr las reglas de negocio
		// fila por fila, sobre las filas reales (se descartan las filas fantasma, ver esFilaVacia).
		const totalRegistros = rawDataCandidato.filter((row) => !esFilaVacia(row)).length;
		if (totalRegistros > MAX_REGISTROS_CARGUE) {
			return { faltantes: [], data: [], hayErrores: false, excedeMaximo: true, totalRegistros };
		}

		const { datos, hayErrores } = validarCandidatoCarga(rawDataCandidato, filaInicio);

		rawCandidatosRowsRef.current = rawDataCandidato
			.map((row: any, index: number) => ({ fila: index + filaInicio, ...row }))
			.filter((row) => !esFilaVacia(row));

		return { faltantes: [], data: datos, hayErrores };
	};

	const procesar = async (uploadedData: any[], nombreArchivo?: string): Promise<ProcesarResultado> => {
		const rawCandidatosRows = rawCandidatosRowsRef.current;
		if (!rawCandidatosRows || rawCandidatosRows.length === 0) {
			return { titulo: 'Carga de Candidatos', mensaje: 'No hay datos válidos para procesar.', nuevasFilas: null };
		}

		// Se mapea el valor digitado en la plantilla (label del select) al código que espera el
		// backend, resolviendo también los campos en cascada (municipio, barrio, localidad, cargo).
		const payload = rawCandidatosRows.map((row: any) => {
			const r = resolverFila(row);
			return {
				fila: row.fila,
				// User + UserComplemento (mismos campos que ya enviaba /addUsersMasivo)
				tipo_doc: r.tipoDocCod ?? row['Tipo de documento'],
				numero_doc: String(row['Número de documento'] ?? '').trim(),
				primer_nome: String(row['Primer nombre'] ?? '').trim(),
				segundo_nome: String(row['Segundo nombre'] ?? '').trim(),
				primer_apelido: String(row['Primer apellido'] ?? '').trim(),
				segundo_apelido: String(row['Segundo apellido'] ?? '').trim(),
				celular: String(row['Teléfono'] ?? '').replace(/\D/g, ''),
				correo: String(row['Correo electrónico'] ?? '').trim(),
				nombre_identitario: String(row['Nombre identitario'] ?? '').trim() || null,
				fecha_nascimiento: r.fechaNacimientoIso,
				// Código único de SEXO -> UserComplemento.genero Y UserCurriculo.sexo (misma fuente).
				sexo_asignado_nacer: r.sexoCod ?? row['Sexo asignado al nacer'],
				// UserCurriculo (Steps 1-4)
				estado_civil: codigoDe(parameters.ESTADOCIVIL, row['Estado civil']),
				identidad_genero: r.identidadGeneroCod,
				orientacion_sexual: codigoDe(parameters.ORIENTACIONSEXUAL, row['Orientación sexual']),
				jefe_hogar: codigoDe(parameters.SINO, row['Jefe o Jefa de hogar']),
				pais_nascimento: r.paisNacCod,
				nacionalidad: r.nacionalidadCod,
				departamento_nacimiento: r.departamentoNacimientoCod ?? null,
				municipio_nacimiento: r.municipioNacimientoCod ?? null,
				libreta_militar: r.libretaCod ?? null,
				tipo_libreta: r.tipoLibretaCod ?? null,
				numero_libreta: String(row['Número de libreta'] ?? '').trim() || null,
				cuenta_sisben: r.cuentaSisbenCod ?? null,
				tipo_sisben: r.tipoSisbenCod ?? null,
				cuidador: r.cuidadorCod ?? null,
				tipo_cuidador: r.tipoCuidadorCod ?? null,
				reconoce_focalizada: r.reconoceCod ?? null,
				grupo_etnico: r.grupoEtnicoCod ?? null,
				// "Grupo indígena" no tiene columna propia en la plantilla oficial: se deja vacío aún
				// cuando Grupo étnico resuelva a Indígena (ver nota en listas.ts).
				grupo_indigena: null,
				persona_discapacidad: r.personaDiscapCod ?? null,
				condicion_discapacidad: r.condicionDiscapCod ?? null,
				tipo_poblacion: r.tipoPoblacionCodigos.length > 0 ? concatArrayToStringJoinBy(r.tipoPoblacionCodigos, ',') : null,
				pais_retornado: r.paisRetornadoCod ?? null,
				otro_telefono: String(row['Otro teléfono'] ?? '').trim() || null,
				observaciones: String(row['Observaciones'] ?? '').trim() || null,
				pais_residencia: r.paisResidenciaCod,
				departamento_residencia: r.departamentoResidenciaCod ?? null,
				municipio_residencia: r.municipioResidenciaCod ?? null,
				localidad: r.localidadEncontrada ? String(r.localidadEncontrada.id) : null,
				bairro_residencia: r.barrioEncontrado ? String(r.barrioEncontrado.id) : null,
				pertence: r.pertenceCod ?? null,
				prestador_preferencia: r.prestadorPreferenciaCod ?? null,
				punta_atencion: r.puntoAtencionCod ?? null,
				local: codigoDe(parameters.VIAPRINCIPAL, row['Vía principal (Dirección)']) ?? null,
				id_coordenada: String(row['Número 1 (Dirección)'] ?? '').trim() || null,
				id_coordenada_02: codigoDe(parameters.VIAPRINCIPALPRIMERALETRA, row['Letra 1 (Dirección)']) ?? null,
				id_coordenada_03: codigoDe(parameters.VIAPRINCIPALBIS, row['BIS 1 (Dirección)']) ?? null,
				id_coordenada_04: codigoDe(parameters.VIAPRINCIPALPRIMERALETRA, row['Letra 2 (Dirección)']) ?? null,
				id_coordenada_05: codigoDe(parameters.VIAPRINCIPALCUADRANTE, row['Cardinalidad 1 (Dirección)']) ?? null,
				id_coordenada_06: String(row['Número 2 (Dirección)'] ?? '').trim() || null,
				id_coordenada_07: codigoDe(parameters.VIAPRINCIPALPRIMERALETRA, row['Letra 3 (Dirección)']) ?? null,
				id_coordenada_08: String(row['Número 3 (Dirección)'] ?? '').trim() || null,
				id_coordenada_09: codigoDe(parameters.VIAPRINCIPALCUADRANTE, row['Cardinalidad 2 (Dirección)']) ?? null,
				direccion_residencia: r.direccionResidencia,
				situacion_laboral_actual: r.situacionLaboralCod,
				possibilidade_viajar: r.possibilidadeViajarCod,
				Interes_ofertas_teletrabajo: r.interesTeletrabajoCod,
				posibilidad_trasladarse: r.posibilidadTrasladarseCod,
				conduces_carro: r.conduceCarroCod,
				categoria_licencia_carro: r.categoriaLicenciaCarroCod ?? null,
				conduces_moto: r.conduceMotoCod,
				categoria_licencia_moto: r.categoriaLicenciaMotoCod ?? null,
				Prop_medio_transporte: r.propMedioTransporteCod,
				perfil_laboral: String(row['Perfil laboral'] ?? '').trim(),
				ultimo_salario: r.ultimoSalarioCod,
				aspiracion_salarial: r.aspiracionSalarialCod,
				aspiracion_salarial_min: r.aspiracionSalarialMinCod,
				complementoDireccion: r.tipoComplementoCod || r.complementoTexto
					? { tipo_complemento: r.tipoComplementoCod ?? null, complemento: r.complementoTexto || null }
					: null,
				id_cargo: r.cargoEncontrado?.id ?? null,
				// UserCurriculo (Steps 5-8): las 3 preguntas de sí/no que habilitan cada bloque.
				tiene_nivel_educativo: r.tieneNivelEducativoCod === CODIGO_SI,
				tiene_experiencia_laboral: r.tieneExperienciaCod === CODIGO_SI,
				tiene_capacitaciones: r.tieneCapacitacionesCod === CODIGO_SI,
				nivel_educativo_max: r.nivelEducativoMaxEncontrado?.sigla ?? null,
				// CurriculoNivelEducativo: 1 registro por fila (ver nota "1 a 1" en el plan de esta sesión).
				nivelEducativo: r.tieneNivelEducativoCod === CODIGO_SI ? {
					nivelEducativo: r.nivelEducativoSigla ?? null,
					tituloObtenido: String(row['Título obtenido'] ?? '').trim() || null,
					tituloHomologado: r.tituloHomologadoEncontrado ? String(r.tituloHomologadoEncontrado.id) : null,
					Institucion: String(row[CAMPO_INSTITUCION_NIVEL_EDUCATIVO] ?? '').trim() || null,
					estadoDelCurso: r.estadoDelCursoCod ?? null,
					fechafinalizacion: r.estadoDelCursoCod === CODIGO_ESTADO_CURSO_GRADUADO ? r.fechaFinalizacionIso : null,
					idObservaciones: String(row[CAMPO_OBSERVACIONES_NIVEL_EDUCATIVO] ?? '').trim() || null,
					areaDesempeno: r.areaDesempenoEncontrada ? String(r.areaDesempenoEncontrada.id) : null,
					paisDelCurso: codigoDe(parameters.PAIS, row['País del título']) ?? null,
					txtNumeroTarjProf: r.tieneTarjetaCod === CODIGO_SI ? String(row['Número de tarjeta profesional'] ?? '').trim() || null : null,
					fechaTarjProf: r.tieneTarjetaCod === CODIGO_SI ? r.fechaTarjProfIso : null,
					nucleoConocimiento: r.nucleoConocimientoEncontrado ? String(r.nucleoConocimientoEncontrado.id) : null,
					interessadoPractica: codigoDe(parameters.SINO, row['Estoy interesado(a) en realizar una práctica empresarial']) === CODIGO_SI,
				} : null,
				// CurriculoExperienciaLaboral: 1 registro por fila.
				experienciaLaboral: r.tieneExperienciaCod === CODIGO_SI ? {
					tpExperienciaLaboral: r.tipoExperienciaCod ?? null,
					sector: r.sectorCod ?? null,
					cargo: String(row['Cargo'] ?? '').trim() || null,
					cargoEquivalente: String(row['Cargo equivalente'] ?? '').trim() || null,
					nombreEmpresa: String(row['Nombre de la empresa'] ?? '').trim() || null,
					telefonoEmpresa: String(row['Teléfono de la empresa'] ?? '').replace(/\D/g, '') || null,
					paisEmpresa: r.paisEmpresaCod ?? null,
					departamentoEmpresa: r.departamentoEmpresaCod ?? null,
					municipioEmpresa: r.municipioEmpresaCod ?? null,
					fechaIngresso: r.fechaIngresoIso,
					trabajandoMismoTrabajo: r.esEmpleoActualCod === CODIGO_SI,
					fechaRetiro: r.esEmpleoActualCod === CODIGO_SI ? null : r.fechaRetiroIso,
					funcionesLogros: String(row['Funciones y logros'] ?? '').trim() || null,
				} : null,
				// CurriculoCapacitaciones: 1 registro por fila.
				capacitacion: r.tieneCapacitacionesCod === CODIGO_SI ? {
					tpCapacitacionCertificacion: r.tipoCapacitacionCod ?? null,
					nombrePrograma: String(row['Nombre del programa'] ?? '').trim() || null,
					instituicion: String(row[CAMPO_INSTITUCION_CAPACITACION] ?? '').trim() || null,
					paisCapacitacione: r.paisCapacitacionCod ?? null,
					estadoCapacitacione: r.estadoCapacitacionCod ?? null,
					fechaCertificacion: r.estadoCapacitacionCod === CODIGO_ESTADO_CERTIFICADO ? r.fechaCertificacionIso : null,
					duracionHoras: row['Duración en horas'] ? Number(row['Duración en horas']) : null,
				} : null,
				// CurriculoIdioma: 1 registro por fila (opcional, no depende de ningún "tiene_X").
				idioma: row['Agregar idioma'] ? {
					id_idioma: r.idIdiomaCod ? Number(r.idIdiomaCod) : null,
					nivel: r.nivelIdiomaCod ? Number(r.nivelIdiomaCod) : null,
				} : null,
				// CurriculoConocimientos ("Herramientas"): 1 registro por fila (opcional).
				herramienta: row['Herramientas'] ? {
					id_tipo_conocimientos: r.tipoHerramientaCod ?? null,
					herramientas: String(row['Herramientas'] ?? '').trim() || null,
					nivel: row['Nivel'] ? Number(row['Nivel']) : null,
				} : null,
			};
		});

		try {
			const response = await createCargueCandidatosPost(payload, nombreArchivo);
			const { creados, fallidos, resultados = [], resumen, mensaje: mensajeBackend } = response?.data ?? {};

			const nuevasFilas = uploadedData.map((fila: any) => {
				const resultado = resultados.find((r: any) => r.fila === fila['N° Fila']);
				if (!resultado) return fila;
				return {
					...fila,
					Estado: resultado.estado === 'OK'
						? `<span class='text-success'>OK</span>`
						: `<span class='text-danger'>ERROR</span>`,
					Observación: resultado.mensaje,
				};
			});

			const resumenCargue: ResumenCargue | undefined = resumen && {
				id: resumen.id,
				fechaCargue: resumen.fecha_cargue,
				nombreArchivo: resumen.nombre_archivo,
				registrosProcesados: resumen.registros_procesados,
				registrosConErrores: resumen.registros_con_errores,
				registrosInsertados: resumen.registros_insertados,
			};

			// "Todo o nada": si el backend rechazó el archivo completo (0 creados, con errores), se
			// muestra el motivo tal cual lo explica el backend en vez del mensaje de éxito parcial.
			const mensaje = (!creados && fallidos)
				? (mensajeBackend || `El archivo fue rechazado: ${fallidos} fila(s) con error, revisa la columna Observación. No se realizó ninguna inserción en la base de datos.`)
				: `Se crearon ${creados ?? 0} candidato(s) con éxito.`;

			return {
				titulo: 'Carga de Candidatos',
				mensaje,
				nuevasFilas,
				resumen: resumenCargue,
			};
		} catch (error: any) {
			const mensaje = error?.response?.data?.error || 'Ocurrió un error procesando la carga masiva de candidatos.';
			return { titulo: 'Error al procesar la carga', mensaje, nuevasFilas: null };
		}
	};

	const reset = () => {
		rawCandidatosRowsRef.current = null;
	};

	return {
		descargarPlantilla,
		procesarArchivoSubido,
		procesar,
		reset,
		cargandoParametros,
	};
};
